import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackfill } from '../BackfillContext';

export default function BackfillWidget() {
  const {
    backfillSymbol, queue, isRunning,
    completedCount, errorCount, progress,
    cancel, dismiss,
  } = useBackfill();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  // Don't render if no backfill in progress
  if (queue.length === 0) return null;

  const runningStep = queue.find(s => s.status === 'running');
  const isDone = !isRunning && !queue.some(s => s.status === 'pending');
  const barColor = errorCount > 0 ? 'var(--yellow)' : isDone ? 'var(--green)' : 'var(--accent)';

  if (collapsed) {
    return (
      <div
        onClick={() => setCollapsed(false)}
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
          width: 48, height: 48, borderRadius: '50%',
          background: 'var(--surface)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}
        title="Show backfill progress"
      >
        {/* Circular progress ring */}
        <svg width="48" height="48" style={{ position: 'absolute', top: 0, left: 0 }}>
          <circle cx="24" cy="24" r="20" fill="none" stroke="var(--border)" strokeWidth="3" />
          <circle
            cx="24" cy="24" r="20" fill="none" stroke={barColor} strokeWidth="3"
            strokeDasharray={`${progress * 1.257} 125.7`}
            strokeLinecap="round"
            transform="rotate(-90 24 24)"
          />
        </svg>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', zIndex: 1 }}>
          {progress}%
        </span>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
      width: 300, background: 'var(--surface)',
      border: '1px solid var(--border)', borderRadius: 12,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: '1px solid var(--border)',
      }}>
        <span
          style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text)' }}
          onClick={() => navigate('/manage')}
          title="Go to Manage Stocks"
        >
          Backfill: {backfillSymbol}
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {isRunning && (
            <button
              onClick={cancel}
              style={{
                background: 'none', border: 'none', color: 'var(--red)',
                cursor: 'pointer', fontSize: 11, fontWeight: 600,
              }}
            >
              Cancel
            </button>
          )}
          {isDone && (
            <button
              onClick={dismiss}
              style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                cursor: 'pointer', fontSize: 11,
              }}
            >
              Dismiss
            </button>
          )}
          <button
            onClick={() => setCollapsed(true)}
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px',
            }}
            title="Minimize"
          >−</button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        width: '100%', height: 4, background: 'var(--border)',
      }}>
        <div style={{
          width: `${progress}%`, height: '100%',
          background: barColor, transition: 'width 0.3s',
        }} />
      </div>

      {/* Status body */}
      <div style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
          <span>{completedCount}/{queue.length} done</span>
          {errorCount > 0 && <span style={{ color: 'var(--red)' }}>{errorCount} failed</span>}
        </div>
        {runningStep && (
          <div style={{
            fontSize: 12, color: 'var(--accent)', fontWeight: 500,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            ⟳ {runningStep.label}
          </div>
        )}
        {isDone && (
          <div style={{ fontSize: 12, color: errorCount > 0 ? 'var(--yellow)' : 'var(--green)', fontWeight: 500 }}>
            {errorCount > 0 ? `Done with ${errorCount} errors` : 'All steps completed!'}
          </div>
        )}
      </div>
    </div>
  );
}
